import React, { useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { Formik, Form, FormikHelpers } from "formik";
import * as Yup from "yup";

import { usePageDetails } from "../../hooks/usePageDetails";
import CreateHeading from "../../components/CreateHeading/CreateHeading";
import { Layout } from "../../layout/PortalLayout/PortalLayout";
import FormInput from "../../components/FormInput/FormInput";
import ToggleSwitch from "../../components/ToggleSwitch/ToggleSwitch";
import FormActionButtons from "../../components/FormActionButtons/FormActionButtons";
import AsyncFormSelect from "../../components/FormSelect/AsyncFormSelect";
import { useLazyGetFamilyListQuery } from "../../app/api/familyApi";
import { useLazyGetBrandListQuery } from "../../app/api/brandApi";
import {
  useCreateModelMutation,
  useEditModelMutation,
  useLazyGetSingleModelDetailsQuery,
} from "../../app/api/modelApi";
import { handleApiResponse } from "../../utils/handleApiResponse";
import { useAppSelector } from "../../redux/store";
import { useFilterManager } from "../../hooks/useFilterManager";

export interface InitialFormValues {
  [key: string]: string | null | boolean;
  model_name: string;
  is_active: boolean;
  family_id: string | null;
  brand_id: string | null;
}

const validationSchema = Yup.object().shape({
  model_name: Yup.string()
    .required("Model name is required")
    .min(2, "Model name must be at least 2 character")
    .max(255, "Model name must be at most 255 characters"),
  is_active: Yup.boolean(),
  family_id: Yup.string().required("Family is required"),
  brand_id: Yup.string().required("Brand is required"),
});

const CreateModel: React.FC = () => {
  const { id, pageType } = usePageDetails();
  const { handleFilterChange } = useFilterManager();
  const navigate = useNavigate();

  const [getBrandListQuery] = useLazyGetBrandListQuery();
  const [getFamilyListQuery] = useLazyGetFamilyListQuery();
  const [createModelMutation] = useCreateModelMutation();
  const [getSingleModelDetailsQuery, getSingleModelDetailsResponse] =
    useLazyGetSingleModelDetailsQuery();
  const [editModelMutation] = useEditModelMutation();

  const persistDropdownSelector = useAppSelector(
    (state) => state.dropdownSelector
  );

  const initialValues: InitialFormValues = {
    model_name: getSingleModelDetailsResponse?.data?.data?.model_name ?? "",
    is_active: getSingleModelDetailsResponse?.data?.data?.is_active ?? true,
    family_id: persistDropdownSelector["family_selector"]?.value ?? "",
    brand_id: persistDropdownSelector["brand_selector"]?.value ?? "",
  };

  const getSingleModelDetails = async () => {
    try {
      await getSingleModelDetailsQuery({
        brand_id: persistDropdownSelector["brand_selector"]?.value,
        family_id: persistDropdownSelector["family_selector"]?.value,
        model_id: id,
      }).unwrap();
    } catch (error: any) {
      console.log(error);
      handleApiResponse({
        response: error,
        type: "error",
        toastId: "Get Model Details Error",
      });
    }
  };

  useEffect(() => {
    if (pageType !== "create") {
      getSingleModelDetails();
    }
    // eslint-disable-next-line
  }, [id]);

  const handleSubmit = async (
    values: InitialFormValues,
    { setFieldError }: FormikHelpers<InitialFormValues>
  ) => {
    const { model_name, family_id, brand_id, is_active } = values;
    const body = {
      model_name,
      ...(pageType === "edit" ? { is_active } : {}),
    };

    const successMessage =
      pageType === "create"
        ? "Model created successfully."
        : "Model updated successfully.";

    try {
      const response =
        pageType === "create"
          ? await createModelMutation({
              body,
              brand_id,
              family_id,
            }).unwrap()
          : await editModelMutation({
              body,
              brand_id,
              family_id,
              model_id: id,
            }).unwrap();
      if (response?.status_code === 200 || response?.status_code === 201) {
        handleApiResponse({
          type: "success",
          message: successMessage,
          toastId: "Create Model",
        });
        navigate("/provisioning/model");
      }
    } catch (error: any) {
      handleApiResponse({
        response: error,
        type: "error",
        toastId: "Create Model Error",
        setFieldError,
      });
      console.log(error);
    }
  };

  return (
    <Layout>
      <CreateHeading label={"Model"} />
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({
          values,
          touched,
          errors,
          handleChange,
          handleBlur,
          setFieldValue,
          setFieldTouched,
          isSubmitting,
          dirty,
        }) => {
          const disable =
            pageType === "create"
              ? !values.model_name || !values.brand_id || !values.family_id
              : !dirty;

          return (
            <>
              <Form className="w-100 mb-5 pb-5">
                <Col sm={6}>
                  {pageType === "view" && (
                    <Row className="my-2">
                      <FormInput
                        label="Model ID"
                        type="text"
                        name="id"
                        placeholder="Enter model name"
                        labelClassName="m-0 fw-500"
                        controlClassName="bg-white fs-15-point-5 h-48"
                        value={
                          getSingleModelDetailsResponse?.data?.data?.id ?? ""
                        }
                        onChange={handleChange}
                        onBlur={handleBlur}
                        required={false}
                        isValid={touched.model_name && !errors.model_name}
                        isInvalid={touched.model_name && !!errors.model_name}
                        touched={touched.model_name}
                        error={errors.model_name}
                        disabled={pageType === "view"}
                      />
                    </Row>
                  )}

                  {pageType === "create" ? (
                    <>
                      {" "}
                      <Row className="my-2">
                        <AsyncFormSelect
                          label="Brand"
                          name="brand_id"
                          onChange={(val: string | null, label: string) => {
                            handleFilterChange({
                              updatedField: "brand_id",
                              persistedSelection: {
                                label,
                                value: val,
                              },
                            });
                          }}
                          fetchQuery={getBrandListQuery}
                          fetchQueryParams={{
                            startKey: "",
                            page_size: 5,
                          }}
                          optionLabelName="brand_name"
                          placeholder="Select Brand"
                          required
                          shouldFetch
                          persistedOption={
                            persistDropdownSelector["brand_selector"]
                              ? {
                                  label:
                                    persistDropdownSelector["brand_selector"]
                                      ?.label,
                                  value:
                                    persistDropdownSelector["brand_selector"]
                                      ?.value,
                                }
                              : null
                          }
                          touched={touched.brand_id}
                          error={errors.brand_id}
                          setFieldTouched={setFieldTouched}
                        />
                      </Row>
                      <Row className="my-2">
                        <AsyncFormSelect
                          label="Family"
                          name="family_id"
                          onChange={(val: string | null, label: string) => {
                            handleFilterChange({
                              updatedField: "family_id",
                              persistedSelection: {
                                label,
                                value: val,
                              },
                            });
                          }}
                          fetchQuery={getFamilyListQuery}
                          fetchQueryParams={{
                            startKey: "",
                            page_size: 5,
                            brand_id: values.brand_id,
                          }}
                          optionLabelName="family_name"
                          placeholder="Select Family"
                          isDisabled={!values.brand_id}
                          required
                          key={`family-select-${values.brand_id}`}
                          shouldFetch={!!values.brand_id?.length}
                          persistedOption={
                            persistDropdownSelector["family_selector"]
                              ? {
                                  label:
                                    persistDropdownSelector["family_selector"]
                                      ?.label,
                                  value:
                                    persistDropdownSelector["family_selector"]
                                      ?.value,
                                }
                              : null
                          }
                          touched={touched.family_id}
                          error={errors.family_id}
                          setFieldTouched={setFieldTouched}
                        />{" "}
                      </Row>
                    </>
                  ) : (
                    <>
                      {" "}
                      <Row className="my-2">
                        <FormInput
                          label="Brand Name"
                          type="text"
                          name="brand_name"
                          placeholder="Brand name"
                          labelClassName="m-0 fw-500"
                          controlClassName="bg-white fs-15-point-5 h-48"
                          value={
                            getSingleModelDetailsResponse?.data?.data
                              ?.brand_name ?? ""
                          }
                          onChange={handleChange}
                          onBlur={handleBlur}
                          required={true}
                          isValid={false}
                          isInvalid={false}
                          touched={false}
                          error={""}
                          disabled
                        />
                      </Row>
                      <Row className="my-2">
                        <FormInput
                          label="Family Name"
                          type="text"
                          name="family_name"
                          placeholder="Family name"
                          labelClassName="m-0 fw-500"
                          controlClassName="bg-white fs-15-point-5 h-48"
                          value={
                            getSingleModelDetailsResponse?.data?.data
                              ?.family_name ?? ""
                          }
                          onChange={handleChange}
                          onBlur={handleBlur}
                          required={true}
                          isValid={false}
                          isInvalid={false}
                          touched={false}
                          error={""}
                          disabled
                        />
                      </Row>
                    </>
                  )}

                  <Row className="my-2">
                    <FormInput
                      label="Model Name"
                      type="text"
                      name="model_name"
                      placeholder="Enter model name"
                      labelClassName="m-0 fw-500"
                      controlClassName="bg-white fs-15-point-5 h-48"
                      value={values.model_name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      required={true}
                      isValid={touched.model_name && !errors.model_name}
                      isInvalid={touched.model_name && !!errors.model_name}
                      touched={touched.model_name}
                      error={errors.model_name}
                      disabled={pageType === "view"}
                    />
                  </Row>
                  {pageType !== "create" && (
                    <Row className="ms-0 mt-4">
                      <ToggleSwitch
                        activeStatusText="Active"
                        inActiveStatusText="Inactive"
                        value={values.is_active}
                        onChange={(val: boolean) => {
                          setFieldValue("is_active", val);
                        }}
                        key={String(values.is_active)}
                        isDisabled={pageType === "view"}
                      />
                    </Row>
                  )}

                  <FormActionButtons
                    disable={disable}
                    isSubmitting={isSubmitting}
                    navigateUrl="/provisioning/model"
                  />
                </Col>
              </Form>
            </>
          );
        }}
      </Formik>
    </Layout>
  );
};

export default CreateModel;
